'use client';

import { useState, useEffect } from 'react';
import { X } from 'lucide-react';

interface ClosePositionModalProps {
  isOpen: boolean;
  onClose: () => void;
  position: {
    coin: string;
    side: 'Long' | 'Short';
    size: number;
    entryPrice: number;
    markPrice: number;
    pnl: number;
    pnlPercent: number;
  };
  onConfirm: (price: string, amount: string, isMarket: boolean) => void;
}

export default function ClosePositionModal({ isOpen, onClose, position, onConfirm }: ClosePositionModalProps) {
  const [activeTab, setActiveTab] = useState<'limit' | 'market'>('limit');
  const [limitPrice, setLimitPrice] = useState('');
  const [amount, setAmount] = useState('');
  const [percentage, setPercentage] = useState(100);
  const [amountInUSDC, setAmountInUSDC] = useState('');

  useEffect(() => {
    if (isOpen && position) {
      // Set initial values
      setLimitPrice(position.markPrice.toFixed(2));
      setAmount(position.size.toFixed(5));
      setAmountInUSDC((position.size * position.markPrice).toFixed(2));
      setPercentage(100);
    }
  }, [isOpen, position]);

  useEffect(() => {
    // Update amount based on percentage
    if (position) {
      const newAmount = ((position.size * percentage) / 100).toFixed(5);
      setAmount(newAmount);
      const price = activeTab === 'market' ? position.markPrice : parseFloat(limitPrice) || position.markPrice;
      setAmountInUSDC((parseFloat(newAmount) * price).toFixed(2));
    }
  }, [percentage, position, limitPrice, activeTab]);

  useEffect(() => {
    // Update USDC value when amount changes
    if (amount && position) {
      const price = activeTab === 'market' ? position.markPrice : parseFloat(limitPrice) || position.markPrice;
      setAmountInUSDC((parseFloat(amount) * price).toFixed(2));
    }
  }, [amount, limitPrice, position, activeTab]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (activeTab === 'limit') {
      if (limitPrice && parseFloat(limitPrice) > 0 && amount) {
        onConfirm(limitPrice, amount, false);
        onClose();
      }
    } else {
      if (amount) {
        onConfirm(position.markPrice.toString(), amount, true);
        onClose();
      }
    }
  };

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center'>
      {/* Backdrop */}
      <div
        className='absolute inset-0 bg-black/70'
        onClick={onClose}
      />

      {/* Modal */}
      <div className='relative w-[480px] rounded-xl bg-[#1a1d24] shadow-2xl'>
        {/* Header */}
        <div className='flex items-center justify-between px-6 py-4'>
          <h3 className='text-lg font-semibold text-white'>
            Close {position.coin} {position.side}
          </h3>
          <button
            onClick={onClose}
            className='p-1 text-gray-400 transition-colors hover:text-white'
          >
            <X className='h-5 w-5' />
          </button>
        </div>

        {/* Content */}
        <div className='space-y-4 px-6 pb-6'>
          {/* Position Info Card */}
          <div className='rounded-lg bg-[#252830] p-4'>
            <div className='mb-3 flex items-center gap-3'>
              <span className='text-lg font-semibold text-white'>{position.coin}</span>
              <span
                className={`rounded px-2 py-0.5 text-xs font-semibold uppercase ${
                  position.side === 'Long'
                    ? 'border border-green-500/30 bg-green-500/20 text-green-400'
                    : 'border border-red-500/30 bg-red-500/20 text-red-400'
                }`}
              >
                {position.side}
              </span>
            </div>

            <div className='grid grid-cols-2 gap-4 text-sm'>
              <div>
                <div className='mb-1 text-gray-500'>Position Size</div>
                <div className='font-mono text-white'>{position.size.toFixed(5)}</div>
              </div>
              <div>
                <div className='mb-1 text-gray-500'>Unrealized PnL</div>
                <div className={`font-mono ${position.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {position.pnl >= 0 ? '+' : ''}
                  {position.pnl.toFixed(2)} ({position.pnlPercent.toFixed(2)}%)
                </div>
              </div>
              <div>
                <div className='mb-1 text-gray-500'>Entry Price</div>
                <div className='font-mono text-white'>{position.entryPrice.toLocaleString()}</div>
              </div>
              <div>
                <div className='mb-1 text-gray-500'>Mid Price</div>
                <div className='font-mono text-white'>{position.markPrice.toLocaleString()}</div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className='flex gap-2'>
            <button
              onClick={() => setActiveTab('limit')}
              className={`flex-1 py-2 text-sm font-medium transition-colors ${
                activeTab === 'limit'
                  ? 'border-b-2 border-orange-400 text-orange-400'
                  : 'border-b-2 border-transparent text-gray-500'
              }`}
            >
              Limit Close
            </button>
            <button
              onClick={() => setActiveTab('market')}
              className={`flex-1 py-2 text-sm font-medium transition-colors ${
                activeTab === 'market'
                  ? 'border-b-2 border-orange-400 text-orange-400'
                  : 'border-b-2 border-transparent text-gray-500'
              }`}
            >
              Market Close
            </button>
          </div>

          {/* Price Input (for Limit only) */}
          {activeTab === 'limit' && (
            <div>
              <div className='mb-2 flex items-center justify-between'>
                <label className='text-sm text-gray-400'>Price</label>
                <span className='text-xs text-gray-500'>{limitPrice} USDC</span>
              </div>
              <div className='flex items-center gap-2'>
                <div className='h-3 w-3 rounded-sm bg-green-400' />
                <div className='h-3 w-3 rounded-sm bg-red-400' />
                <input
                  type='text'
                  value={limitPrice}
                  onChange={e => setLimitPrice(e.target.value)}
                  placeholder='0.00'
                  className='flex-1 border-b border-gray-600 bg-transparent pb-1 text-sm text-white outline-none'
                />
              </div>
            </div>
          )}

          {/* Amount Input */}
          <div>
            <div className='mb-3 flex items-center justify-between'>
              <div className='flex items-center gap-4'>
                <input
                  type='text'
                  value={amount}
                  onChange={e => {
                    setAmount(e.target.value);
                    // Update percentage based on amount
                    const pct = (parseFloat(e.target.value) / position.size) * 100;
                    setPercentage(Math.min(100, Math.max(0, pct || 0)));
                  }}
                  className='w-24 rounded border border-gray-700 bg-[#252830] px-3 py-2 text-sm text-white outline-none focus:border-gray-600'
                />
                <span className='text-sm text-gray-400'>{position.coin}</span>
                <input
                  type='text'
                  value={amountInUSDC}
                  onChange={e => {
                    setAmountInUSDC(e.target.value);
                    // Update amount based on USDC value
                    const price =
                      activeTab === 'market' ? position.markPrice : parseFloat(limitPrice) || position.markPrice;
                    const newAmount = (parseFloat(e.target.value) / price).toFixed(5);
                    setAmount(newAmount);
                    const pct = (parseFloat(newAmount) / position.size) * 100;
                    setPercentage(Math.min(100, Math.max(0, pct || 0)));
                  }}
                  className='w-24 rounded border border-gray-700 bg-[#252830] px-3 py-2 text-sm text-white outline-none focus:border-gray-600'
                />
                <span className='text-sm text-gray-400'>USDC</span>
              </div>
            </div>

            {/* Percentage Slider */}
            <div className='space-y-2'>
              <div className='relative'>
                <input
                  type='range'
                  min='0'
                  max='100'
                  value={percentage}
                  onChange={e => setPercentage(parseInt(e.target.value))}
                  className='slider h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-700'
                  style={{
                    background: `linear-gradient(to right, #ff6b35 0%, #ff6b35 ${percentage}%, #374151 ${percentage}%, #374151 100%)`,
                  }}
                />
                <div className='pointer-events-none absolute -top-1 left-0 flex w-full justify-between'>
                  <div
                    className='h-3 w-3 rounded-full bg-orange-500'
                    style={{ marginLeft: '25%' }}
                  />
                  <div
                    className='h-3 w-3 rounded-full bg-orange-500'
                    style={{ marginLeft: '8%' }}
                  />
                  <div
                    className='h-3 w-3 rounded-full bg-orange-500'
                    style={{ marginLeft: '8%' }}
                  />
                  <div
                    className='h-3 w-3 rounded-full bg-orange-500'
                    style={{ marginLeft: '8%' }}
                  />
                  <div
                    className='h-3 w-3 rounded-full bg-orange-500'
                    style={{ marginLeft: '8%' }}
                  />
                  <div
                    className='h-3 w-3 rounded-full border-2 border-white bg-orange-500'
                    style={{ marginLeft: '8%' }}
                  />
                </div>
              </div>
              <div className='flex justify-between text-xs text-gray-500'>
                <span>25%</span>
                <span>33%</span>
                <span>50%</span>
                <span>66%</span>
                <span>75%</span>
                <button
                  onClick={() => setPercentage(100)}
                  className={`rounded px-2 py-0.5 ${
                    percentage === 100 ? 'bg-orange-500 text-white' : 'bg-gray-700 text-gray-400'
                  }`}
                >
                  100%
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Button */}
        <div className='px-6 pb-6'>
          <button
            onClick={handleConfirm}
            disabled={!amount || parseFloat(amount) <= 0}
            className={`w-full rounded-lg py-3 text-sm font-semibold transition-colors ${
              position.side === 'Long'
                ? 'bg-[#ff6b35] text-white hover:bg-[#ff5522]'
                : 'bg-[#00d395] text-black hover:bg-[#00c385]'
            } ${!amount || parseFloat(amount) <= 0 ? 'cursor-not-allowed opacity-50' : ''}`}
          >
            {activeTab === 'limit' ? 'Limit Close' : 'Market Close'}
          </button>
        </div>
      </div>
    </div>
  );
}
